import { useQuery } from '@tanstack/react-query';
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Container, Row, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Route, Routes } from 'react-router-dom';

import { getOutlets } from '../../apis/queries';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import PaginationControls from '../../components/Pagination/PaginationControls';
import { Outlet } from '../../types';
import AnalyticsHeader from '../../widgets/Header';
import CreateOutletModal from './CreateOutletModal';
import OutletDetail from './ViewAndEditDetail/ViewAndEditDetail';

const CreateOutletsButton = ({ onClick }: { onClick: () => void }) => {
  return <Button onClick={onClick}> New Outlet </Button>;
};

export const OUTLETS_QUERY_KEY = ['outlets'];

const columnHelper = createColumnHelper<Outlet>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('phoneNumber', {
    header: 'Phone',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('address', {
    header: 'Address',
    cell: info => info.getValue(),
    footer: info => info.column.id
  })
  // columnHelper.accessor('isActive', {
  //   header: 'Active',
  //   cell: info => info.getValue(),
  //   footer: info => info.column.id
  // })
];

interface OutletTableProps {
  query: Record<string, unknown>;
}

const limit = 100;

const OutletsTable = ({ query }: OutletTableProps) => {
  const [page, setPage] = useState(1);
  const compositeQueryKey = query
    ? [...OUTLETS_QUERY_KEY, { ...query, page, limit }]
    : [OUTLETS_QUERY_KEY, { page, limit }];
  const { data, isLoading } = useQuery({
    queryKey: compositeQueryKey,
    queryFn: () => getOutlets({ ...query, page, limit })
  });
  const outlets = data?.results;
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [query]);

  const { count } = data ?? {};

  const totalPages = Math.ceil(count! / limit);

  const nextPage = data?.nextPage;
  const prevPage = data?.prevPage;

  const onCreateButtonClicked = () => {
    setShowCreateModal(true);
  };

  const onCreateModalClosed = () => {
    setShowCreateModal(false);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <>
      {showCreateModal && <CreateOutletModal show={showCreateModal} handleClose={onCreateModalClosed} />}
      <AnalyticsHeader
        title="Outlets"
        headerButton={<CreateOutletsButton onClick={onCreateButtonClicked} />}
        tabs={tabRoutes}
      />
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs={12}>
            {isLoading ? (
              <Loader />
            ) : outlets && outlets.length > 0 ? (
              <>
                <TableList outlets={outlets!} columns={columns} />
                <PaginationControls
                  currentPage={page}
                  totalPages={totalPages ?? 0}
                  onPageChange={handlePageChange}
                  nextPage={nextPage ?? null}
                  prevPage={prevPage ?? null}
                />
              </>
            ) : (
              <EmptyTableState message="No outlets yet." />
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

const tabRoutes = [
  {
    title: 'Active',
    path: 'active',
    tabLink: '/outlets/active',
    element: <OutletsTable query={{ isActive: true }} />
  },
  {
    title: 'Inactive',
    path: 'inactive',
    tabLink: '/outlets/inactive',
    element: <OutletsTable query={{ isActive: false }} />
  }
];

const outletRoutes = [
  {
    path: '/:status/:id',
    element: <OutletDetail />
  }
];

const Outlets = () => {
  return (
    <>
      <div className="main-content">
        <Routes>
          {tabRoutes.map(({ path, element, title }) => (
            <Route key={title} path={path} element={element} />
          ))}
          {outletRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Routes>
      </div>
    </>
  );
};

export default Outlets;

interface TableListProps {
  outlets: Outlet[];
  columns: AccessorKeyColumnDef<Outlet, string>[];
}

const TableList = ({ outlets, columns }: TableListProps) => {
  const tableInstance = useReactTable({ data: outlets!, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <Card>
      <Table size="sm" className="card-table table-nowrap" hover responsive>
        <thead>
          {tableInstance.getHeaderGroups().map(headerGroup => {
            return (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th colSpan={header.colSpan} key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody>
          {tableInstance.getRowModel().rows.map(row => {
            return (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => {
                  return (
                    <td key={cell.id}>
                      <Link to={`../outlet/${row.original._id}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Link>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
};
